import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, money } from "@/lib/api";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export const AIConcierge = () => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(uid);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Welcome to Elite STR. I'm your personal concierge — tell me your dream getaway and I'll find the perfect home and plan your stay." },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/concierge", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", text: data.reply, properties: data.properties || [] }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Apologies — I had trouble responding. Please try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button data-testid="concierge-toggle" onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full btn-ink shadow-2xl">
        {open ? <X size={22} /> : <MessageCircle size={22} strokeWidth={1.5} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="concierge-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }} transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-6 z-50 flex h-[520px] w-[92vw] max-w-sm flex-col overflow-hidden rounded-3xl card-lux">
            <div className="flex items-center gap-2 border-b border-black/5 bg-ink px-5 py-4 text-white">
              <Sparkles size={18} className="text-gold" strokeWidth={1.5} />
              <div>
                <div className="text-sm font-semibold">AI Concierge</div>
                <div className="text-[11px] text-white/50">Powered by Elite STR</div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-sand px-4 py-4 no-scrollbar">
              {messages.map((m, i) => (
                <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-ink text-white" : "border border-black/5 bg-white text-ink"}`}>
                    {m.text}
                  </div>
                  {m.properties?.length > 0 && (
                    <div className="mt-2 w-full space-y-2">
                      {m.properties.map((p) => (
                        <div key={p.id} data-testid={`concierge-rec-${p.id}`}
                          className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white p-2">
                          <img src={p.hero} alt={p.name} className="h-12 w-12 shrink-0 rounded-xl object-cover" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold text-ink">{p.name}</div>
                            <div className="text-[11px] text-taupe">{money(p.nightly_rate)}/night · ★ {p.rating}</div>
                          </div>
                          <button data-testid={`concierge-reserve-${p.id}`}
                            onClick={() => { setOpen(false); nav(`/rentals/${p.id}`); }}
                            className="shrink-0 rounded-full btn-ink px-3 py-1.5 text-xs font-semibold">
                            Reserve
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="rounded-2xl border border-black/5 bg-white px-4 py-3">
                    <span className="flex gap-1">
                      {[0, 1, 2].map((d) => (
                        <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-taupe"
                          style={{ animationDelay: `${d * 120}ms` }} />
                      ))}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 border-t border-black/5 bg-white p-3">
              <input data-testid="concierge-input" value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about homes, dates, activities…"
                className="flex-1 rounded-full bg-cream px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-camel" />
              <button data-testid="concierge-send" onClick={send} disabled={busy}
                className="flex h-10 w-10 items-center justify-center rounded-full btn-ink disabled:opacity-50">
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
